import React from "react";
import {
  EdgePreset,
  ThemeConfig,
} from "../../../../service/BuyerConfiguration/BuyerAppDashboard/homeThemesService";

/** Garland along the top of the dash and the bottom edge shape, as SVG. */

const GARLAND_DEFAULTS: Record<string, string[]> = {
  toran: ["#FF8C00", "#E53935", "#FFC300", "#2E7D32"],
  lights: ["#FFD700", "#FF3B30", "#34C759", "#0A84FF"],
  marigold: ["#FF8C00", "#FFC300", "#FF5E00"],
  bells: ["#FFD700", "#B8860B"],
  stars: ["#FFD700"],
  mango: ["#2E7D32", "#66BB6A"],
};

const starPoints = (cx: number, cy: number, r: number) => {
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const rr = i % 2 === 0 ? r : r * 0.45;
    pts.push(`${(cx + Math.cos(a) * rr).toFixed(1)},${(cy + Math.sin(a) * rr).toFixed(1)}`);
  }
  return pts.join(" ");
};

/** y of a wire that sags between anchors every `span` px. */
const sagY = (x: number, y0: number, span: number, depth: number) =>
  y0 + depth * Math.sin((Math.PI * (((x % span) + span) % span)) / span);

export const Garland: React.FC<{
  garland: ThemeConfig["garland"];
  width: number;
  animate: boolean;
}> = ({ garland, width, animate }) => {
  if (garland.preset === "none") return null;
  const colors = garland.colors.length ? garland.colors : GARLAND_DEFAULTS[garland.preset] ?? ["#FFD700"];
  const col = (i: number) => colors[i % colors.length];
  const swayStyle = (i: number): React.CSSProperties =>
    animate && garland.sway
      ? {
          transformBox: "fill-box",
          transformOrigin: "50% 0%",
          animation: `tsx-garland-sway ${2.6 + (i % 3) * 0.4}s ease-in-out ${(i % 5) * 0.2}s infinite alternate`,
        }
      : {};
  const twinkle = (i: number): React.CSSProperties =>
    animate ? { animation: `tsx-twinkle ${1.1 + (i % 4) * 0.3}s ease-in-out ${(i % 6) * 0.17}s infinite alternate` } : {};

  const items: React.ReactNode[] = [];
  let wire: React.ReactNode = null;
  const H = 48;

  switch (garland.preset) {
    case "toran": {
      wire = <path d={`M0,4 Q${width / 2},10 ${width},4`} stroke="#8D5524" strokeWidth={1.5} fill="none" />;
      const step = 18;
      for (let i = 0, x = 9; x < width; i++, x += step) {
        const y = 4 + 6 * Math.sin((Math.PI * x) / width);
        items.push(
          <polygon key={i} points={`${x - 8},${y} ${x + 8},${y} ${x},${y + 17}`} fill={col(i)} stroke="rgba(0,0,0,0.15)" strokeWidth={0.5} style={swayStyle(i)} />,
        );
      }
      break;
    }
    case "lights": {
      const span = 90;
      let d = "M0,3";
      for (let x = 0; x <= width; x += 6) d += ` L${x},${sagY(x, 3, span, 13).toFixed(1)}`;
      wire = <path d={d} stroke="#2B2B2B" strokeWidth={1.2} fill="none" />;
      for (let i = 0, x = 10; x < width; i++, x += 18) {
        const y = sagY(x, 3, span, 13);
        items.push(
          <g key={i} style={twinkle(i)}>
            <rect x={x - 1.8} y={y} width={3.6} height={3} fill="#333" />
            <ellipse cx={x} cy={y + 7} rx={3.4} ry={5} fill={col(i)} />
            <ellipse cx={x} cy={y + 7} rx={7} ry={8} fill={col(i)} opacity={0.18} />
          </g>,
        );
      }
      break;
    }
    case "marigold": {
      const span = width / 3;
      for (let i = 0, x = 0; x <= width; i++, x += 8) {
        items.push(<circle key={`c${i}`} cx={x} cy={sagY(x, 4, span, 14)} r={5} fill={col(i)} />);
      }
      // hanging strands at the anchors
      for (let a = 0; a <= 3; a++) {
        const x = Math.min(width - 6, Math.max(6, a * span));
        items.push(
          <g key={`s${a}`} style={swayStyle(a)}>
            {[0, 1, 2, 3].map((k) => (
              <circle key={k} cx={x} cy={9 + k * 8} r={4.4} fill={col(k + a)} />
            ))}
            <circle cx={x} cy={9 + 4 * 8} r={3} fill="#2E7D32" />
          </g>,
        );
      }
      break;
    }
    case "bells": {
      wire = <line x1={0} y1={3} x2={width} y2={3} stroke={col(1)} strokeWidth={1.5} />;
      for (let i = 0, x = 30; x < width; i++, x += 60) {
        const len = i % 2 ? 14 : 22;
        items.push(
          <g key={i} style={swayStyle(i)}>
            <line x1={x} y1={3} x2={x} y2={3 + len} stroke={col(1)} strokeWidth={1} />
            <path
              d={`M${x - 7},${3 + len + 12} Q${x - 7},${3 + len} ${x},${3 + len} Q${x + 7},${3 + len} ${x + 7},${3 + len + 12} Z`}
              fill={col(0)}
              stroke={col(1)}
              strokeWidth={0.8}
            />
            <circle cx={x} cy={3 + len + 13.5} r={2} fill={col(1)} />
          </g>,
        );
      }
      break;
    }
    case "stars": {
      wire = <line x1={0} y1={2} x2={width} y2={2} stroke={col(0)} strokeOpacity={0.5} strokeWidth={1} />;
      for (let i = 0, x = 16; x < width; i++, x += 30) {
        const len = [10, 22, 15, 28][i % 4];
        items.push(
          <g key={i} style={swayStyle(i)}>
            <line x1={x} y1={2} x2={x} y2={2 + len} stroke={col(i)} strokeOpacity={0.6} strokeWidth={0.8} />
            <polygon points={starPoints(x, 2 + len + 5, 5.5)} fill={col(i)} style={twinkle(i)} />
          </g>,
        );
      }
      break;
    }
    case "mango": {
      wire = <path d={`M0,4 Q${width / 2},9 ${width},4`} stroke="#8D5524" strokeWidth={1.5} fill="none" />;
      for (let i = 0, x = 7; x < width; i++, x += 14) {
        const y = 4 + 5 * Math.sin((Math.PI * x) / width);
        items.push(
          <g key={i} style={swayStyle(i)}>
            <ellipse cx={x} cy={y + 10} rx={3.6} ry={10} fill={col(i)} />
            <line x1={x} y1={y + 1} x2={x} y2={y + 19} stroke="rgba(0,0,0,0.18)" strokeWidth={0.6} />
          </g>,
        );
      }
      break;
    }
  }

  return (
    <svg width={width} height={H} style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none", overflow: "visible" }}>
      {wire}
      {items}
    </svg>
  );
};

/** Height the bottom edge adds below the content. */
export const edgeHeight = (edge: ThemeConfig["edge"]) =>
  edge.preset === "wave" || edge.preset === "scallop" || edge.preset === "zigzag" ? Math.round(12 * edge.size) : 0;

/** Corner radius for the "round" edge. */
export const edgeRadius = (edge: ThemeConfig["edge"]) => (edge.preset === "round" ? Math.round(22 * edge.size) : 0);

/** Curve of the edge (0 = top of the strip, amp = deepest point of the dash). */
const edgeCurve = (preset: EdgePreset, x: number, amp: number, size: number) => {
  switch (preset) {
    case "wave": {
      const p = 90 * size;
      return amp / 2 + (amp / 2) * Math.sin((2 * Math.PI * x) / p);
    }
    case "scallop": {
      const p = 28 * size;
      const u = ((x % p) - p / 2) / (p / 2);
      return amp * Math.sqrt(Math.max(0, 1 - u * u));
    }
    case "zigzag": {
      const p = 20 * size;
      return amp * (1 - Math.abs((2 * (x % p)) / p - 1));
    }
    default:
      return amp;
  }
};

/** Draws the page colour below the edge curve, so the dash ends in that shape. */
export const EdgeShape: React.FC<{
  edge: ThemeConfig["edge"];
  width: number;
  pageColor: string;
}> = ({ edge, width, pageColor }) => {
  const amp = edgeHeight(edge);
  if (!amp) return null;
  let d = `M0,${amp + 1} L0,${edgeCurve(edge.preset, 0, amp, edge.size).toFixed(2)}`;
  for (let x = 2; x <= width; x += 2) d += ` L${x},${edgeCurve(edge.preset, x, amp, edge.size).toFixed(2)}`;
  d += ` L${width},${amp + 1} Z`;
  return (
    <svg
      width={width}
      height={amp + 1}
      style={{ position: "absolute", left: 0, bottom: -1, pointerEvents: "none", display: "block" }}
    >
      <path d={d} fill={pageColor} />
    </svg>
  );
};
